import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import {
  getTrafficLightLevel,
  isLikelyHfss,
  NutritionalValues,
  NutritionSource,
  ProductNutritionalInfo,
  TrafficLightLevel,
  UK_REFERENCE_INTAKE,
} from './types';

interface NutritionalDataTabProps {
  nutritionalInfo: ProductNutritionalInfo;
  onChange: (nutritionalInfo: ProductNutritionalInfo) => void;
  disabled?: boolean;
  /** Which screen is authoring this record - stamped onto the data on change so
   * downstream consumers can tell recipe-derived and supplier-provided data apart. */
  source: NutritionSource;
}

type NutrientColumn = 'per100g' | 'perServing';

const trafficLightColor: Record<TrafficLightLevel, string> = {
  low: '#00703C', // FSA green
  medium: '#EF8F00', // FSA amber
  high: '#D8262C', // FSA red
};

/** Reference Intake %s shown on the UK front-of-pack summary, keyed to the same nutrients the
 * FSA traffic-light scheme covers, plus energy (which is %RI but not traffic-lighted). */
const RI_ROWS: { key: keyof NutritionalValues; label: string; unit: string; ri: number }[] = [
  { key: 'energyKcal', label: 'Energy', unit: 'kcal', ri: UK_REFERENCE_INTAKE.energyKcal },
  { key: 'fat', label: 'Fat', unit: 'g', ri: UK_REFERENCE_INTAKE.fat },
  { key: 'saturates', label: 'Saturates', unit: 'g', ri: UK_REFERENCE_INTAKE.saturates },
  { key: 'sugars', label: 'Sugars', unit: 'g', ri: UK_REFERENCE_INTAKE.sugars },
  { key: 'salt', label: 'Salt', unit: 'g', ri: UK_REFERENCE_INTAKE.salt },
];

const NutritionalDataTab: FC<NutritionalDataTabProps> = ({ nutritionalInfo, onChange, disabled, source }) => {
  const { t } = useTranslation('common');

  const rows: { key: keyof NutritionalValues; label: string; unit: string }[] = [
    { key: 'energyKj', label: t('nutritionEnergyKj', 'Energy (kJ)'), unit: 'kJ' },
    { key: 'energyKcal', label: t('nutritionEnergyKcal', 'Energy (kcal)'), unit: 'kcal' },
    { key: 'fat', label: t('nutritionFat', 'Fat'), unit: 'g' },
    { key: 'saturates', label: t('nutritionSaturates', 'of which saturates'), unit: 'g' },
    { key: 'carbohydrate', label: t('nutritionCarbohydrate', 'Carbohydrate'), unit: 'g' },
    { key: 'sugars', label: t('nutritionSugars', 'of which sugars'), unit: 'g' },
    { key: 'fibre', label: t('nutritionFibre', 'Fibre'), unit: 'g' },
    { key: 'protein', label: t('nutritionProtein', 'Protein'), unit: 'g' },
    { key: 'salt', label: t('nutritionSalt', 'Salt'), unit: 'g' },
  ];

  const handleValueChange = (column: NutrientColumn, key: keyof NutritionalValues, value: string) => {
    onChange({
      ...nutritionalInfo,
      source,
      [column]: {
        ...nutritionalInfo[column],
        [key]: value === '' ? undefined : Number(value),
      },
    });
  };

  const sourceLabel = source === NutritionSource.Recipe
    ? t('nutritionSourceRecipe', 'Recipe-derived')
    : t('nutritionSourceSupplier', 'Supplier-provided');

  const hfss = isLikelyHfss(nutritionalInfo.per100g);

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="textLG"
            sx={{ fontWeight: 600 }}>
            {t('nutritionalInfo', 'Nutritional Info')}
          </Typography>
          <Chip label={sourceLabel}
            size="small"
            color="primary"
            variant="outlined" />
          {hfss && (
            <Tooltip title={t('nutritionHfssTooltip', '2 or more of fat, saturates, sugars or salt are "high" per 100g (FSA traffic light thresholds) - likely HFSS (High in Fat, Sugar or Salt).')}>
              <Chip icon={<WarningAmber sx={{ fontSize: 16 }} />}
                label={t('nutritionHfssLabel', 'Likely HFSS')}
                size="small"
                color="error" />
            </Tooltip>
          )}
        </Box>

        {/* UK front-of-pack style summary - %RI per serving, colour-coded per FSA traffic
            light thresholds evaluated on the per-100g values. */}
        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 1, display: 'block' }}>
            {t('nutritionUkSummaryTitle', 'UK Nutrition Summary (per serving)')}
          </Typography>
          <Stack direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            {RI_ROWS.map((row) => {
              const perServingValue = nutritionalInfo.perServing[row.key];
              const level = row.key === 'energyKcal'
                ? undefined
                : getTrafficLightLevel(row.key as 'fat' | 'saturates' | 'sugars' | 'salt', nutritionalInfo.per100g[row.key]);
              const percentRi = perServingValue != null ? Math.round((perServingValue / row.ri) * 100) : undefined;
              const color = level ? trafficLightColor[level] : '#6B7280';
              return (
                <Box key={row.key}
                  sx={{
                    minWidth: 88,
                    textAlign: 'center',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderTop: `4px solid ${color}`,
                    p: 1.25,
                  }}>
                  <Typography variant="textXS"
                    color="text.secondary"
                    sx={{ display: 'block' }}>{row.label}</Typography>
                  <Typography variant="textMD"
                    fontWeight={700}
                    sx={{ display: 'block' }}>
                    {perServingValue != null ? `${perServingValue}${row.unit}` : '—'}
                  </Typography>
                  <Typography variant="textXS"
                    fontWeight={600}
                    sx={{ display: 'block', color }}>
                    {percentRi != null ? `${percentRi}% RI` : t('nutritionNoData', 'No data')}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
      <Grid container
        spacing={4}
        sx={{ mb: 4 }}>
        <Grid item
          xs={12}
          md={4}>
          <TextField
            label={t('nutritionServingSize', 'Serving Size')}
            value={nutritionalInfo.servingSize ?? ''}
            onChange={(e) => onChange({ ...nutritionalInfo, source, servingSize: e.target.value })}
            disabled={disabled}
            placeholder={t('nutritionServingSizePlaceholder', 'e.g. 100g')}
            fullWidth
          />
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('nutritionNutrient', 'Nutrient')}</TableCell>
              <TableCell>{t('nutritionPer100g', 'Per 100g')}</TableCell>
              <TableCell>{t('nutritionPerServing', 'Per Serving')}</TableCell>
              <TableCell>{t('nutritionPercentRi', '% RI (per serving)')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const riRow = RI_ROWS.find((r) => r.key === row.key);
              const perServingValue = nutritionalInfo.perServing[row.key];
              const percentRi = riRow && perServingValue != null
                ? Math.round((perServingValue / riRow.ri) * 100)
                : undefined;
              return (
                <TableRow key={row.key}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={nutritionalInfo.per100g[row.key] ?? ''}
                      onChange={(e) => handleValueChange('per100g', row.key, e.target.value)}
                      disabled={disabled}
                      size="small"
                      InputProps={{ endAdornment: <InputAdornment position="end">{row.unit}</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={nutritionalInfo.perServing[row.key] ?? ''}
                      onChange={(e) => handleValueChange('perServing', row.key, e.target.value)}
                      disabled={disabled}
                      size="small"
                      InputProps={{ endAdornment: <InputAdornment position="end">{row.unit}</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell>
                    {percentRi != null ? `${percentRi}%` : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NutritionalDataTab;
